import { DateTime } from 'luxon';

/**
 * Timezone por defecto para la aplicación (Chile - America/Santiago)
 * GMT-3 en verano (con DST), GMT-4 en invierno
 */
export const DEFAULT_TIMEZONE = 'America/Santiago';

/**
 * Parsea un string UTC de la base de datos a DateTime de Luxon
 * Backend devuelve fechas en formato UTC: "2026-01-25 18:30:00"
 * Esta función interpreta ese UTC y lo convierte a la timezone del evento
 * @param utcString - String formato "YYYY-MM-DD HH:mm:ss" en UTC
 * @param timezone - Zona horaria del evento (ej: "America/Santiago")
 * @returns DateTime de Luxon en la timezone del evento
 * @example
 * // Backend devuelve: "2026-01-25 18:30:00" (UTC)
 * // parseUTCDate(value, "America/Santiago") → DateTime 2026-01-25 15:30 GMT-3
 */
export const parseUTCDate = (utcString, timezone = DEFAULT_TIMEZONE) => {
  if (!utcString) return null;
  
  // Parsear como UTC y convertir a timezone del evento
  const dt = DateTime.fromSQL(utcString, { zone: 'utc' });
  return dt.isValid ? dt.setZone(timezone) : null;
};

/**
 * Parsea un string ISO 8601 (recibido del backend) a DateTime de Luxon
 * @param isoString - String formato ISO 8601 con timezone (ej: "2026-01-21T20:35:00-03:00")
 * @param timezone - Zona horaria opcional para interpretar la fecha
 * @returns DateTime de Luxon en la timezone especificada o local del navegador
 */
export const parseISODate = (isoString, timezone = null) => {
  if (!isoString) return null;
  
  const dt = timezone 
    ? DateTime.fromISO(isoString, { zone: timezone })
    : DateTime.fromISO(isoString);
  return dt.isValid ? dt : null;
};

/**
 * Parsea un string de fecha SQL (YYYY-MM-DD HH:mm:ss) a DateTime de Luxon
 * @param sqlString - String formato SQL (ej: "2026-01-21 20:35:00")
 * @param timezone - Zona horaria en la que interpretar la fecha (default: America/Santiago)
 * @returns DateTime de Luxon en la timezone especificada
 */
export const parseSQL = (sqlString, timezone = DEFAULT_TIMEZONE) => {
  if (!sqlString) return null;
  
  const dt = DateTime.fromSQL(sqlString, { zone: timezone });
  return dt.isValid ? dt : null;
};

/**
 * Convierte un DateTime a formato SQL
 * @param dt - DateTime de Luxon
 * @returns String en formato SQL (YYYY-MM-DD HH:mm:ss)
 */
export const toSQL = (dt) => {
  if (!dt || !dt.isValid) return null;
  return dt.toSQL();
};

/**
 * Formatea un DateTime para mostrar en UI (fecha y hora)
 * Ejemplo: "21/01/2026 20:35"
 * @param dt - DateTime de Luxon o string ISO 8601
 * @param format - Formato personalizado (opcional)
 * @returns String formateado para UI
 */
export const formatDateTime = (dt, format = 'dd/MM/yyyy HH:mm') => {
  if (!dt) return '';
  
  const dateTime = typeof dt === 'string' ? parseISODate(dt) : dt;
  if (!dateTime || !dateTime.isValid) return '';
  
  return dateTime.toFormat(format);
};

/**
 * Formatea solo la fecha (sin hora)
 * Ejemplo: "21/01/2026"
 * @param dt - DateTime de Luxon o string ISO 8601
 * @returns String formateado
 */
export const formatDate = (dt) => {
  return formatDateTime(dt, 'dd/MM/yyyy');
};

/**
 * Formatea solo la hora (sin fecha)
 * Ejemplo: "20:35"
 * @param dt - DateTime de Luxon o string ISO 8601
 * @returns String formateado
 */
export const formatTime = (dt) => {
  return formatDateTime(dt, 'HH:mm');
};

/**
 * Obtiene la fecha/hora actual en la timezone del navegador
 * @returns DateTime de Luxon
 */
export const now = () => {
  return DateTime.now();
};

/**
 * Verifica si un DateTime (o string ISO) es posterior a otro
 * @param dt1 - Primer DateTime o string ISO
 * @param dt2 - Segundo DateTime o string ISO
 * @returns true si dt1 > dt2
 */
export const isAfter = (dt1, dt2) => {
  const date1 = typeof dt1 === 'string' ? parseISODate(dt1) : dt1;
  const date2 = typeof dt2 === 'string' ? parseISODate(dt2) : dt2;
  
  if (!date1 || !date2) return false;
  return date1 > date2;
};

/**
 * Verifica si un DateTime (o string ISO) es anterior a otro
 * @param dt1 - Primer DateTime o string ISO
 * @param dt2 - Segundo DateTime o string ISO
 * @returns true si dt1 < dt2
 */
export const isBefore = (dt1, dt2) => {
  const date1 = typeof dt1 === 'string' ? parseISODate(dt1) : dt1;
  const date2 = typeof dt2 === 'string' ? parseISODate(dt2) : dt2;
  
  if (!date1 || !date2) return false;
  return date1 < date2;
};

/**
 * Calcula si un ticket/evento está vigente (no ha expirado)
 * @param expirationDate - Fecha de expiración (DateTime o string ISO)
 * @returns true si aún está vigente
 */
export const isVigente = (expirationDate) => {
  return isAfter(expirationDate, now());
};

/**
 * Formatea una fecha en formato relativo ("hace 2 horas", "en 3 días", etc.)
 * @param dt - DateTime o string ISO
 * @returns String con formato relativo
 */
export const formatRelative = (dt) => {
  if (!dt) return '';
  
  const dateTime = typeof dt === 'string' ? parseISODate(dt) : dt;
  if (!dateTime || !dateTime.isValid) return '';
  
  return dateTime.toRelative() || '';
};

/**
 * Calcula la diferencia entre dos fechas en días
 * @param dt1 - Primer DateTime o string ISO
 * @param dt2 - Segundo DateTime o string ISO (por defecto: ahora)
 * @returns Número de días de diferencia
 */
export const diffInDays = (dt1, dt2 = now()) => {
  const date1 = typeof dt1 === 'string' ? parseISODate(dt1) : dt1;
  const date2 = typeof dt2 === 'string' ? parseISODate(dt2) : dt2;
  
  if (!date1 || !date2) return 0;
  return Math.floor(date1.diff(date2, 'days').days);
};

/**
 * Convierte un DateTime a objeto Date nativo de JavaScript (si es necesario para librerías)
 * @param dt - DateTime de Luxon o string ISO
 * @returns Date de JavaScript
 */
export const toJSDate = (dt) => {
  if (!dt) return null;
  
  const dateTime = typeof dt === 'string' ? parseISODate(dt) : dt;
  if (!dateTime || !dateTime.isValid) return null;
  
  return dateTime.toJSDate();
};
